'use client';

/**
 * ChargePayPal Component
 *
 * Headless processor for PayPal payments (WEB only).
 * Navigates a pre-opened popup to the PayPal URL and waits for a postMessage
 * with the payment result. Does NOT render any UI — parent uses
 * ChargeStatusDisplay for display.
 *
 * The popup window is created synchronously in the user's click handler
 * (via createPaymentPopup) and passed here as `clickContext` to satisfy
 * Safari's popup-blocker requirements.
 *
 * Flow:
 * - Receive pre-opened PaymentPopup via clickContext prop
 * - Build PayPal purchase URL and navigate the popup to it
 * - Wait for postMessage from the payment window with ok/error message
 * - If user closes the popup manually -> onCancel
 *
 * NOTE: For mobile apps, PayPal is handled differently — the parent component
 * should intercept the payment action and send `on_pay` message to the app.
 */

import React, { useEffect, useRef } from 'react';
import queryString from 'query-string';
import { usePurchaseLog } from '../../config/PurchaseFlowConfigProvider';
import { usePurchaseAnalytics } from '../../analytics';
import { browserContext, getClientPlatformForAPI } from '../../utils/platform';
import type { ChargeProcessorProps } from '../../types';
import type { PaymentPopup } from '../../utils/paymentPopup';

type PayPalMessage = {
  id?: number;
  okMessage?: string;
  errorMessage?: string;
  errorType?: string;
  recoverable?: boolean;
  retriable?: boolean;
  errorOrigin?: string;
  errorPathname?: string;
  [key: string]: any;
};

const buildUrl = (path: string, params: Record<string, any>): string => {
  const qs = queryString.stringify(params);
  const fullPath = qs ? `${ path }?${ qs }` : path;
  return `${ window.location.origin }${ fullPath }`;
};

/**
 * True when message is the final one (ok or error). First message may only have conversionEventId.
 */
const isPaymentComplete = (data: any): boolean => !!(data?.okMessage || data?.errorMessage);

export const ChargePayPal: React.FC<ChargeProcessorProps> = ({
  amount,
  withBonus = false,
  purchaseType,
  advisorId,
  clickSource,
  purchaseContext,
  recover = false,
  recoverBaseUrl,
  fxRate,
  currency,
  onStatusChange,
  onSuccess,
  onError,
  onCancel,
  viaSite,
  extraParams,
  clickContext,
  startSignal = 0
}) => {
  const completedRef = useRef(false);
  const purchaseStartedTrackedRef = useRef(false);
  const log = usePurchaseLog();
  const analytics = usePurchaseAnalytics();

  useEffect(() => {
    if (!startSignal) return undefined;
    completedRef.current = false;

    onStatusChange?.('processing', 'Opening PayPal...');

    const parsedExtraParams = extraParams
      ? queryString.parse(extraParams)
      : {};

    const url = recover
      ? `${ recoverBaseUrl }?${ queryString.stringify({
        recover, use_reference_transaction: true, with3ds: true, ...browserContext
      }) }`
      : buildUrl(`/my/app_credits/${ amount }/process_credits`, {
        with_payment_sources: true,
        platform: getClientPlatformForAPI(),
        purchase_type: purchaseType?.toLowerCase(),
        intended_advisor_id: advisorId,
        ...(clickSource ? { click_source: clickSource } : {}),
        use_reference_transaction: true,
        with3ds: true,
        with_bonus: withBonus,
        context: purchaseContext,
        ...(viaSite && { via_site: true }),
        ...(currency && { currency }),
        ...(fxRate && { fx_rate: fxRate }),
        ...parsedExtraParams,
        ...browserContext
      });

    const paymentPopup = clickContext as PaymentPopup | undefined;

    if (!paymentPopup?.window) {
      onError({ success: false, errorMessage: 'Could not open PayPal window' });
      return undefined;
    }

    log('info', 'PayPal', 'Navigating PayPal popup', { url });
    paymentPopup.navigateTo(url);
    onStatusChange?.('processing', 'Complete payment in PayPal window...');

    paymentPopup.waitForResult({
      onMessage: (data) => {
        if (data?.conversionEventId && !purchaseStartedTrackedRef.current) {
          purchaseStartedTrackedRef.current = true;
          analytics.setConversionEventId(data.conversionEventId);
          analytics.trackPurchaseStarted();
        }
      },
      checkComplete: isPaymentComplete
    }).then((res) => {
      if (completedRef.current) return;

      paymentPopup.close();

      if (res.type === 'closed') {
        completedRef.current = true;
        onCancel?.();
        return;
      }

      completedRef.current = true;
      const {
        okMessage, localOkMessage, errorMessage, userCreditId, extraEventParams, cashbackAmount,
        errorType, recoverable, retriable, errorOrigin, errorPathname, spendingLimits,
        pathname, threeDAuthResult
      } = res.data as PayPalMessage;

      analytics.setExtraEventParams(extraEventParams);

      if (localOkMessage || okMessage) {
        onSuccess({
          success: true, userCreditId, okMessage: localOkMessage || okMessage, cashbackAmount, pathname, threeDAuthResult
        });
      } else if (errorMessage) {
        onError({
          success: false,
          errorMessage,
          errorType,
          recoverable,
          retriable,
          errorOrigin,
          errorPathname,
          spendingLimits
        });
      } else {
        onError({
          success: false,
          errorMessage: 'Payment could not be completed'
        });
      }
    });

    return () => {
      completedRef.current = true;
      paymentPopup.cleanup();
    };
  }, [startSignal, log]);

  // Headless component - no UI rendered
  // PayPal uses external popup window, not React DOM
  return null;
};
